import { useState } from 'react';
import classNames from 'classnames';
import { useSettings } from 'contexts/SettingContext';
import useQuickAddToCartTracking from 'hooks/tracking/useQuickAddToCartTracking';
import getShopifyData from 'lib/shopify/getShopifyData';
import { useAddItemsToCart } from 'hooks';
import { useRouter } from 'next/router';
import useProductAddedTracking from 'hooks/tracking/useProductAddedTracking';
import { getShopifyGid } from 'lib/shopify/shopifyGid';
import { useGlobalModules } from 'contexts';
import { getSelectedSalonNo, getStorageCustomerSalonNo } from 'lib/shopify';
import Icon from '../icon';
import styles from './quickCartSection.module.scss';

const QuickCartSection = ({ productData, itemIndex, pageTitle, locale, outOfStock, showIcon }) => {
  const [isLoading, setIsLoading] = useState(false);
  const addItems = useAddItemsToCart();
  const { addProduct, setSaveItemSelectionList } = useSettings();
  const globalModules = useGlobalModules();
  const { outOfStock: outOfStockLabel } = globalModules.translations.product;
  const { addToCart, addingToCart } = globalModules.translations.button;
  const salonNo = getStorageCustomerSalonNo(locale);
  const selectedSalonNo = getSelectedSalonNo(locale);
  const router = useRouter();

  const handleAddToCart = async () => {
    setIsLoading(true);

    const shopifyIDs = [`gid://shopify/Product/${productData.shopifyData?.variants[0].product_id}`];

    const actualShopifyProducts = await getShopifyData({ productIds: shopifyIDs, locale });

    const allProducts = actualShopifyProducts.reduce((prev, shopifyProduct) => {
      const relatedProducts = shopifyProduct.relatedProducts || [];
      return [...prev, shopifyProduct.variantId, ...relatedProducts];
    }, []);

    const productsDataForCheckout = [...new Set(allProducts)].map(id => ({
      variantId: id,
      quantity: 1,
    }));

    await addItems(productsDataForCheckout);

    const prod = actualShopifyProducts[0];
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useQuickAddToCartTracking({
      product: {
        name: prod.productTitle,
        id: productData.shopifyData.variants[0]?.product_id,
        productSpecs: productData.productSpecs,
        price: prod.productPrice,
        index: itemIndex,
        quantity: 1,
        image_url: productData?.image?.baseUrl,
        compare_at_price: prod.productCompareAtPrice,
      },
      list: `Category: ${pageTitle}`,
      setSaveItemSelectionList,
      locale,
    });
    // Universal Analytics
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useProductAddedTracking({
      affiliatedSalonNo: salonNo,
      selectedSalonNo,
      product: {
        name: prod.productTitle,
        id: productData.shopifyData.variants[0]?.product_id,
        brand: 'Keune',
        category: productData.productSpecs?.find(
          ({ type }) =>
            type === 'Producttype' || type === 'Type de produit' || type === 'Product type'
        )?.value,
        price: prod.productPrice,
        discount:
          prod.productCompareAtPrice !== null
            ? prod.productCompareAtPrice - prod.productPrice
            : null,
        quantity: 1,
      },
      location: { action: router },
    });

    if (!prod.isFreeItem) {
      addProduct({
        title: prod.productTitle,
        slug: prod.productHandle,
        variantId: getShopifyGid('ProductVariant', productData.shopifyData.variants[0].id),
      });
    }

    setIsLoading(false);
  };

  if (productData.shopifyData) {
    return (
      <>
        <div className={classNames(styles.cartSection)}>
          <button
            type="button"
            disabled={outOfStock}
            className={styles.addToCartButton}
            aria-label={productData?.title ? `${addToCart}: ${productData.title}` : undefined}
            onClick={event => {
              event.stopPropagation();
              event.preventDefault();
              handleAddToCart();
            }}
          >
            {showIcon && !outOfStock && <Icon type="cart" className={styles.cartIcon} />}
            {/* eslint-disable-next-line no-nested-ternary */}
            {isLoading ? addingToCart : outOfStock ? outOfStockLabel : addToCart}
          </button>
        </div>
      </>
    );
  }

  return null;
};

export default QuickCartSection;
